import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from "react";
import "@/App.css";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Radio, Settings, Info, X, EyeOff, Eye,
  Volume2, VolumeX, Signal, Share2, Copy, Check, LocateFixed, HelpCircle,
} from "lucide-react";
import { Toaster, toast } from "sonner";

import { LoadingSequence } from "./components/LoadingSequence";
import { Briefing } from "./components/Briefing";
import { AdminConsole } from "./components/AdminConsole";
import { ProximityTakeover } from "./components/ProximityTakeover";
import { CullVote } from "./components/CullVote";
import { FleetPanel } from "./components/FleetPanel";
import { ActivityPanel } from "./components/ActivityPanel";
import { EmoteBar, EMOTES, getEmoteIcon } from "./components/EmoteBar";
import { TraceTracker } from "./components/TraceTracker";
import { Crosshair } from "lucide-react";
import { renderToStaticMarkup } from "react-dom/server";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const API = `${BACKEND_URL}/api`;

const getWatcherId = () => {
  try {
    let id = localStorage.getItem("tracker_watcher_id");
    if (!id) {
      id = (crypto && crypto.randomUUID && crypto.randomUUID()) ||
           `w-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("tracker_watcher_id", id);
    }
    return id;
  } catch {
    return `w-${Date.now()}`;
  }
};
const WATCHER_ID = getWatcherId();

// Settings survive a reload. Privacy mode matters most here: it is the control
// that stops broadcasting your location — including when the proximity takeover
// switches it on for you — and it used to quietly reset to "visible" on every
// refresh, which is the wrong direction for a setting to fail in.
const SETTINGS_KEY = "tracker_settings";
const SAVED_SETTINGS = (() => {
  try { return JSON.parse(localStorage.getItem(SETTINGS_KEY)) || {}; }
  catch { return {}; }
})();

// Marking windows sit on fixed 10-minute boundaries of wall-clock time, so the
// client can derive the phase itself and always show a live countdown. (It used
// to tick a server snapshot down and clamp at zero — one missed poll and the
// timer stuck at 0:00 forever.) ELIGIBILITY still comes from the server, keyed
// to the viewer's IP/session, so this can't be gamed by fiddling the clock.
const MARK_PERIOD_S = 600;
const MARK_WINDOW_LEN_S = 60;

const markWindowPhase = (nowMs) => {
  const t = nowMs / 1000;
  const into = t % MARK_PERIOD_S;
  const open = into < MARK_WINDOW_LEN_S;
  return {
    open,
    windowId: Math.floor(t / MARK_PERIOD_S),
    opensIn: open ? 0 : MARK_PERIOD_S - into,
    closesIn: open ? MARK_WINDOW_LEN_S - into : 0,
  };
};

const fmtMS = (secs) => {
  const s = Math.max(0, Math.ceil(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/** Copy to the clipboard, with a fallback for insecure contexts and older
 *  browsers where navigator.clipboard is absent. Resolves on success. */
const copyText = (text) => {
  const write = navigator.clipboard && navigator.clipboard.writeText
    ? navigator.clipboard.writeText(text)
    : Promise.reject(new Error("no clipboard api"));
  return write.catch(() => new Promise((resolve, reject) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      resolve();
    } catch (e) { reject(e); }
  }));
};

const _markIconCache = new Map();
const createMarkIcon = (stranger, label) => {
  const key = `${stranger ? 1 : 0}|${label}`;
  if (_markIconCache.has(key)) return _markIconCache.get(key);
  const icon = L.divIcon({
    className: `mark-marker${stranger ? " stranger" : ""}`,
    html: `<div class="mark-inner"><div class="mark-ring"></div><div class="mark-core"></div><div class="mark-label">${label}</div></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 31],   // base of the projection sits on the point
  });
  _markIconCache.set(key, icon);
  return icon;
};

const DEFAULT_LAT = -19.2590;
const DEFAULT_LNG = 146.8169;

// The box the whole simulation lives in: every base city is in Queensland and
// the map is clamped to this rectangle. One definition, used for both Leaflet's
// maxBounds and the out-of-region check, so the two can't drift apart.
const OPERATING_BOUNDS = [[-40, 128], [-6, 160]];
const inOperatingArea = (loc) =>
  !!loc &&
  loc.lat >= OPERATING_BOUNDS[0][0] && loc.lat <= OPERATING_BOUNDS[1][0] &&
  loc.lng >= OPERATING_BOUNDS[0][1] && loc.lng <= OPERATING_BOUNDS[1][1];

// Proximity takeover tuning.
const SELF_MARK_WARN_M = 400;         // marking this close to yourself warns first
const PROXIMITY_M = 240;              // "nearby" radius (metres)
const PROXIMITY_DECAY_RANGE = 420;   // link fades to zero this far beyond the radius
const PROXIMITY_COOLDOWN_MS = 4 * 60 * 1000;
// Failsafe only. The overlay promises the view stays locked while the asset is
// in the area, so this must NOT be a short timer — the link bar (distance-based)
// is what normally ends it. This is purely so a stuck asset can't trap someone.
const PROXIMITY_MAX_MS = 4 * 60 * 1000;
const PROXIMITY_LOCK_MS = 4700;      // intro sequence length before the link bar matters
const metersBetween = (a, b) => {
  const R = 6371000, toRad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * toRad, dLng = (b.lng - a.lng) * toRad;
  const la = a.lat * toRad, lb = b.lat * toRad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la) * Math.cos(lb) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

// Warm the browser HTTP cache with the CARTO tiles for the initial viewport
// *before* Leaflet mounts, so the basemap paints instantly under the loader
// instead of popping in tile-by-tile. Fires once at module import.
function preloadCartoTiles(lat, lng, zoom) {
  if (typeof window === "undefined") return;
  const n = Math.pow(2, zoom);
  const cx = Math.floor(((lng + 180) / 360) * n);
  const rad = (lat * Math.PI) / 180;
  const cy = Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n
  );
  const r = window.devicePixelRatio > 1 ? "@2x" : "";
  const subs = ["a", "b", "c", "d"];
  let i = 0;
  // Cover a viewport-sized region plus a one-tile buffer ring (7 wide × 5 tall).
  for (let dx = -3; dx <= 3; dx++) {
    for (let dy = -2; dy <= 2; dy++) {
      const x = cx + dx;
      const y = cy + dy;
      if (x < 0 || y < 0 || x >= n || y >= n) continue;
      const s = subs[i++ % subs.length];
      const img = new Image();
      img.src = `https://${s}.basemaps.cartocdn.com/dark_all/${zoom}/${x}/${y}${r}.png`;
    }
  }
}
preloadCartoTiles(DEFAULT_LAT, DEFAULT_LNG, 13);

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// --- Icons ----------------------------------------------------------------

let _userIcon = null;
const createUserIcon = () => {
  if (_userIcon) return _userIcon;
  _userIcon = L.divIcon({
    className: "user-marker",
    html: `<div class="user-marker-inner"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
  return _userIcon;
};

// Traces paint as an AREA, not a point. A pin gave the spot away — the whole
// mechanic is that you walk there and hunt for it, so the map says "somewhere
// in here" and the tracker screen does the rest.
const TRACE_RING_RADIUS_M = 150;
const TRACE_RING_OFFSET_MAX_M = 60;

// ...and the centre of the ring isn't the answer either: it's nudged off the
// true position by a seeded amount. Pure function of the trace id, so every
// device draws the identical circle and it never jitters between polls — a
// ring that moved each tick could be averaged out to find the middle.
// The offset stays well inside the radius, so the trace is always in the ring.
const traceRingCentre = (t) => {
  let h = 0;
  for (let i = 0; i < t.id.length; i++) h = (h * 31 + t.id.charCodeAt(i)) >>> 0;
  const bearing = ((h % 360) * Math.PI) / 180;
  const dist = 15 + ((h >>> 9) % (TRACE_RING_OFFSET_MAX_M - 15 + 1));
  const dLat = (dist / 111000) * Math.cos(bearing);
  const dLng = ((dist / 111000) * Math.sin(bearing)) / Math.cos((t.lat * Math.PI) / 180);
  return [t.lat + dLat, t.lng + dLng];
};

let _adminTraceIcon = null;
const createAdminTraceIcon = () => {
  if (_adminTraceIcon) return _adminTraceIcon;
  _adminTraceIcon = L.divIcon({
    className: "admin-trace-marker",
    html: `<div class="admin-trace-dot" data-testid="admin-trace-dot"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
  return _adminTraceIcon;
};

// Cache agent icons by their visual state. Reusing the same divIcon object
// lets Leaflet just reposition the marker instead of rebuilding its DOM every
// frame — essential with a whole fleet tweening at ~30fps.
const _agentIconCache = new Map();
const createAgentIcon = (id, code, isFlying, restoredPulse, isGold, isDrone) => {
  const key = `${id}|${code}|${isFlying ? 1 : 0}|${restoredPulse ? 1 : 0}|${isGold ? 1 : 0}|${isDrone ? 1 : 0}`;
  const cached = _agentIconCache.get(key);
  if (cached) return cached;
  const icon = L.divIcon({
    className: `vehicle-marker agent-marker-live${isFlying ? " flying" : ""}${restoredPulse ? " signal-restored" : ""}${isGold ? " gold" : ""}${isDrone ? " drone" : ""}`,
    html: `
      <div class="agent-marker-inner" data-testid="agent-marker-${id}">
        <div class="agent-marker-ring"></div>
        <div class="agent-marker-dot"></div>
        <div class="agent-marker-code">${code}</div>
      </div>
    `,
    // Symmetric 40x40 icon anchored dead-center: the ring + dot are centered
    // at (20,20), so the vehicle sits exactly on its road coordinate at every
    // zoom. The code label hangs below without affecting the anchor.
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
  _agentIconCache.set(key, icon);
  return icon;
};

const _footstepIconCache = new Map();
const createFootstepIcon = (index) => {
  const cached = _footstepIconCache.get(index);
  if (cached) return cached;
  const icon = L.divIcon({
    className: "footstep-marker",
    html: `<div class="footstep-inner" data-testid="footstep-${index}"><svg viewBox="0 0 24 24" width="14" height="14" fill="rgba(255,255,255,0.78)" stroke="none">
      <path d="M7 2c1.1 0 2 .9 2 2 0 1.5-1 4-2 4s-2-2.5-2-4c0-1.1.9-2 2-2zm10 2c1.1 0 2 .9 2 2 0 1.5-1 4-2 4s-2-2.5-2-4c0-1.1.9-2 2-2zM5.5 12c1.5 0 3 1 3 3s-1 4-3 4-3-2.5-3-4 1.5-3 3-3zm12 2c1.5 0 3 1 3 3s-1 4-3 4-3-2.5-3-4 1.5-3 3-3z"/>
    </svg></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
  _footstepIconCache.set(index, icon);
  return icon;
};

const _emoteIconCache = new Map();
const createEmoteIcon = (emoteId) => {
  if (_emoteIconCache.has(emoteId)) return _emoteIconCache.get(emoteId);
  const meta = getEmoteIcon(emoteId);
  // The server's allowed-emote set is a superset of the bar's palette (it still
  // accepts `ping`, which the bar dropped when MARK became its own mechanic).
  // Returning null for those made them phantoms: they toasted, chirped, bumped
  // the counter and drew an off-screen arrow, but never painted on the map.
  // Anything unrecognised falls back to the generic signal glyph.
  const { Icon } = meta || { Icon: Radio };
  // Render the lucide icon to static HTML so Leaflet can use it inside a divIcon
  const svg = renderToStaticMarkup(<Icon size={20} strokeWidth={1.8} />);
  const icon = L.divIcon({
    className: "emote-marker",
    html: `<div class="emote-marker-inner" data-testid="emote-marker-${emoteId}">${svg}</div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
  _emoteIconCache.set(emoteId, icon);
  return icon;
};

// --- Helpers --------------------------------------------------------------

// Graticule: faint lat/lng grid over the whole panable area, built once as a
// single multi-polyline so it costs one Leaflet layer. Surveillance-globe vibe.
const GRATICULE_POSITIONS = (() => {
  const lines = [];
  for (let lat = -40; lat <= -6; lat += 2) lines.push([[lat, 128], [lat, 160]]);
  for (let lng = 128; lng <= 160; lng += 2) lines.push([[-40, lng], [-6, lng]]);
  return lines;
})();

const formatTime = (iso) => {
  if (!iso) return "--:--:--";
  try { return new Date(iso).toLocaleTimeString("en-AU", { hour12: false, timeZone: "Australia/Brisbane" }); }
  catch { return iso; }
};

/** Popup body for a marking spot, with an expandable (i) detail panel. */
const MarkPopupBody = ({ mark }) => {
  const [openInfo, setOpenInfo] = useState(false);
  const expiresInS = Math.max(0, Math.round((mark.expires_ts || 0) - Date.now() / 1000));
  const placed = mark.created_iso ? new Date(mark.created_iso) : null;
  return (
    <div className="popup-content mark-popup">
      <div className="mark-popup-head">
        <strong className={mark.by_stranger ? "text-red" : "text-gold"}>{mark.name}</strong>
        <button
          className="mark-info-btn"
          onClick={() => setOpenInfo((v) => !v)}
          title="Marker details"
          data-testid={`mark-info-${mark.id}`}
        >
          i
        </button>
      </div>
      <span className="popup-sub">
        {mark.by_stranger ? "unknown signal" : "marked spot"} · {mark.region}
      </span>
      {openInfo && (
        <div className="mark-info-panel" data-testid="mark-info-panel">
          <div><span>ORIGIN</span><strong>{mark.by_stranger ? "UNIDENTIFIED" : "VIEWER"}</strong></div>
          <div><span>REGION</span><strong>{mark.region}</strong></div>
          <div><span>PLACED</span><strong>{placed ? placed.toLocaleTimeString("en-AU", { hour12: false }) : "—"}</strong></div>
          <div><span>EXPIRES</span><strong>{Math.floor(expiresInS / 60)}m {expiresInS % 60}s</strong></div>
          <div><span>COORD</span><strong>{mark.lat.toFixed(4)}, {mark.lng.toFixed(4)}</strong></div>
          <p className="mark-info-note">
            Assets sometimes investigate marked ground. If the map gets crowded,
            viewers vote one marker off.
          </p>
        </div>
      )}
    </div>
  );
};

const MapController = ({ flyTo, followLocation, zoomingRef, coordsRef, centerRef, onMapClick, bootPhase }) => {
  const map = useMap();
  const lastFollow = useRef(null);

  // CRITICAL: Leaflet can cache the container size as 0×0 if it initializes
  // before layout/paint settles. Because the container size then never CHANGES,
  // Leaflet's ResizeObserver never fires to correct it — so the map stays 0×0,
  // zoom anchors at the corner, and markers/tiles scale on a phantom surface
  // (the "markers don't snap, zoom makes it worse" bug). Force a re-measure
  // after mount and again after layout has definitely settled.
  useEffect(() => {
    const fix = () => { try { map.invalidateSize({ animate: false }); } catch { /* map gone */ } };
    fix();
    const raf = requestAnimationFrame(fix);
    const timers = [80, 300, 800, 1600, 3000].map((ms) => setTimeout(fix, ms));
    window.addEventListener("resize", fix);
    window.addEventListener("orientationchange", fix);
    document.addEventListener("visibilitychange", fix);
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", fix);
      window.removeEventListener("orientationchange", fix);
      document.removeEventListener("visibilitychange", fix);
    };
  }, [map]);

  // Re-measure the instant the loader clears — the map may have initialized
  // to 0×0 while the loading overlay was up.
  useEffect(() => {
    if (bootPhase === "live") {
      try { map.invalidateSize({ animate: false }); } catch { /* map gone */ }
      const t = setTimeout(() => { try { map.invalidateSize({ animate: false }); } catch { /* map gone */ } }, 120);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [bootPhase, map]);

  // Operator console: when an action is armed, the next map click reports its
  // coordinates. Listener only exists while armed so normal use costs nothing.
  useEffect(() => {
    if (!onMapClick) return undefined;
    const h = (e) => onMapClick(e.latlng);
    map.on("click", h);
    return () => map.off("click", h);
  }, [map, onMapClick]);

  // Live map-center readout + center ref (for crosshair marking): written
  // straight into the DOM/ref so panning never triggers a react re-render.
  useEffect(() => {
    const update = () => {
      const c = map.getCenter();
      if (centerRef) centerRef.current = { lat: c.lat, lng: c.lng };
      const el = coordsRef && coordsRef.current;
      if (el) {
        const lat = Math.abs(c.lat).toFixed(4) + (c.lat < 0 ? "S" : "N");
        const lng = Math.abs(c.lng).toFixed(4) + (c.lng < 0 ? "W" : "E");
        el.textContent = `${lat}  ${lng}  Z${map.getZoom()}`;
      }
    };
    update();
    map.on("move zoomend", update);
    return () => map.off("move zoomend", update);
  }, [map, coordsRef, centerRef]);

  // Flag zoom animations so the fleet tween pauses and lets Leaflet's native
  // zoom animation carry the markers with the tiles (otherwise per-frame
  // setLatLng snaps them to the target-zoom pixel mid-animation).
  useEffect(() => {
    if (!zoomingRef) return undefined;
    const start = () => { zoomingRef.current = true; };
    const end = () => { zoomingRef.current = false; };
    // Also pause for user drags (dragstart/dragend, NOT programmatic panTo —
    // follow mode must keep tweening). On mobile the per-frame marker writes
    // are the main thing fighting a pinch/pan gesture.
    map.on("zoomstart", start);
    map.on("zoomend", end);
    map.on("dragstart", start);
    map.on("dragend", end);
    return () => {
      map.off("zoomstart", start); map.off("zoomend", end);
      map.off("dragstart", start); map.off("dragend", end);
    };
  }, [map, zoomingRef]);

  useEffect(() => {
    if (flyTo) map.flyTo([flyTo.lat, flyTo.lng], flyTo.zoom || 14, { duration: 1.5 });
  }, [flyTo, map]);

  useEffect(() => {
    if (!followLocation) { lastFollow.current = null; return; }
    const last = lastFollow.current;
    if (!last ||
        Math.abs(last.lat - followLocation.lat) > 1e-6 ||
        Math.abs(last.lng - followLocation.lng) > 1e-6) {
      map.panTo([followLocation.lat, followLocation.lng], { animate: true, duration: 0.8 });
      lastFollow.current = followLocation;
    }
  }, [followLocation, map]);

  return null;
};

/**
 * Small red arrows pinned to the viewport edge for any emote that's
 * currently active but outside the visible map bounds. Recomputes on
 * every map move/zoom and every emote list change.
 */
const EmoteEdgeIndicators = ({ emotes }) => {
  const map = useMap();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!map) return undefined;
    const bump = () => setTick((t) => t + 1);
    map.on("move zoom moveend zoomend", bump);
    return () => map.off("move zoom moveend zoomend", bump);
  }, [map]);
  // tick just forces a re-render on map events
  void tick;

  if (!map || !emotes || emotes.length === 0) return null;
  const bounds = map.getBounds();
  const size = map.getSize();          // {x, y} pixel size of the map container
  const margin = 26;                   // keep arrows this far from the edge
  const cx = size.x / 2;
  const cy = size.y / 2;
  const halfW = cx - margin;
  const halfH = cy - margin;

  const offscreen = emotes.filter((e) => !bounds.contains([e.lat, e.lng]));
  if (offscreen.length === 0) return null;

  const arrows = offscreen.map((e) => {
    const p = map.latLngToContainerPoint([e.lat, e.lng]);
    const dx = p.x - cx;
    const dy = p.y - cy;
    // Scale so the arrow sits on the edge box (whichever axis clips first)
    const scale = Math.min(halfW / Math.max(1, Math.abs(dx)), halfH / Math.max(1, Math.abs(dy)));
    const x = cx + dx * scale;
    const y = cy + dy * scale;
    const angleDeg = Math.atan2(dy, dx) * 180 / Math.PI; // 0 = right
    return { id: e.id, x, y, angleDeg, emoji_id: e.emoji_id };
  });

  return (
    <div className="emote-edge-layer" data-testid="emote-edge-layer">
      {arrows.map((a) => (
        <div
          key={a.id}
          className="emote-edge-arrow"
          data-testid={`emote-edge-${a.emoji_id}`}
          style={{
            left: `${a.x}px`,
            top: `${a.y}px`,
            transform: `translate(-50%, -50%) rotate(${a.angleDeg}deg)`,
          }}
          title={`Emote off-screen`}
        >
          <span className="emote-edge-arrow-head" />
        </div>
      ))}
    </div>
  );
};

function generateFootsteps(center) {
  const pts = [];
  const seed = Math.floor(center.lat * 1000) + Math.floor(center.lng * 1000);
  let rnd = seed;
  const pseudo = () => { rnd = (rnd * 9301 + 49297) % 233280; return rnd / 233280; };
  for (let i = 0; i < 6; i++) {
    const angle = pseudo() * 2 * Math.PI;
    const dist = 150 + pseudo() * 300;
    const lat = center.lat + (dist / 111000) * Math.cos(angle);
    const lng = center.lng + (dist / 111000) * Math.sin(angle) / Math.cos(center.lat * Math.PI / 180);
    pts.push({ lat, lng });
  }
  return pts;
}

// --- Route-following interpolation (keeps markers exactly on the road) -----

function haversineKm(a, b) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function polylineLength(route) {
  let total = 0;
  for (let i = 1; i < route.length; i++) total += haversineKm(route[i - 1], route[i]);
  return total;
}

// Point at `km` arc-length along the polyline.
function pointAtDistance(route, km) {
  if (km <= 0) return { lat: route[0].lat, lng: route[0].lng };
  let acc = 0;
  for (let i = 1; i < route.length; i++) {
    const seg = haversineKm(route[i - 1], route[i]);
    if (acc + seg >= km) {
      const r = seg > 0 ? (km - acc) / seg : 0;
      return {
        lat: route[i - 1].lat + r * (route[i].lat - route[i - 1].lat),
        lng: route[i - 1].lng + r * (route[i].lng - route[i - 1].lng),
      };
    }
    acc += seg;
  }
  const last = route[route.length - 1];
  return { lat: last.lat, lng: last.lng };
}

// Arc-length of the point on the route closest to `pt` (re-anchors to the
// server's reported position each poll).
function nearestDistanceAlong(route, pt) {
  let best = Infinity;
  let bestKm = 0;
  let acc = 0;
  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1];
    const b = route[i];
    const seg = haversineKm(a, b);
    const dx = b.lng - a.lng;
    const dy = b.lat - a.lat;
    const len2 = dx * dx + dy * dy;
    let t = len2 ? ((pt.lng - a.lng) * dx + (pt.lat - a.lat) * dy) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    const proj = { lat: a.lat + t * dy, lng: a.lng + t * dx };
    const d = haversineKm(proj, pt);
    if (d < best) { best = d; bestKm = acc + seg * t; }
    acc += seg;
  }
  return bestKm;
}

// --- Main App -------------------------------------------------------------

function App() {
  const [bootPhase, setBootPhase] = useState("loading");
  const [tilesReady, setTilesReady] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [activities, setActivities] = useState([]);
  // Live traces. Fetched as the full list rather than just a count: the server
  // caps them at 10, so the list costs the same request the count did and lets
  // them be drawn on the map instead of hiding behind the TRACES tab.
  const [traces, setTraces] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [now, setNow] = useState(new Date());
  const [watchersOnline, setWatchersOnline] = useState(0);

  const [showSettings, setShowSettings] = useState(false);
  const [privacyMode, setPrivacyMode] = useState(!!SAVED_SETTINGS.privacyMode);
  const [showPrivacyInfo, setShowPrivacyInfo] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(SAVED_SETTINGS.soundEnabled !== false);
  const [trackedOpen, setTrackedOpen] = useState(true);
  const [activityOpen, setActivityOpen] = useState(false);
  const [traceScreenOpen, setTraceScreenOpen] = useState(false);
  const [regionNoticeDismissed, setRegionNoticeDismissed] = useState(false);
  const [briefingOpen, setBriefingOpen] = useState(false);

  const [flyTo, setFlyTo] = useState(null);
  const [follow, setFollow] = useState(false);
  const coordsRef = useRef(null);

  // Hidden operator console: 8 taps on the logo, then the passphrase.
  const [adminPrompt, setAdminPrompt] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminArmed, setAdminArmed] = useState(null); // {mode:"move"|"dispatch"|"trace-add", agentId}
  const [adminPw, setAdminPw] = useState("");
  const [adminTraces, setAdminTraces] = useState([]);
  const logoTapsRef = useRef({ n: 0, t: 0 });
  const audioCtxRef = useRef(null);
  const prevIdsRef = useRef(new Set());
  const zoomingRef = useRef(false);
  const agentsRef = useRef([]);
  // Phone-booth arrival sound: previous per-asset state + global cooldown so
  // simultaneous arrivals can never stack or replay.
  const prevAssetsRef = useRef({});
  const lastPhoneTalkRef = useRef(0);
  const sessionId = WATCHER_ID.slice(-6).toUpperCase();

  // Smooth client-side tweening between server polls — one tween per agent id
  const [displayLocations, setDisplayLocations] = useState({});
  const tweensRef = useRef({});

  // Active emotes broadcast by other watchers
  const [emotes, setEmotes] = useState([]);
  const seenEmoteIds = useRef(new Set());
  // Lifetime for the local expiry sweep, taken from the server's own tunable so
  // the two can't drift. Defaults to the backend's EMOTE_TTL_SECONDS.
  const emoteTtlMsRef = useRef(5000);

  // Marking spots (shared server state) + a 10-min cookie-backed cooldown.
  const [markSpots, setMarkSpots] = useState([]);
  const seenMarkIds = useRef(new Set());
  const [markServerState, setMarkServerState] = useState(null); // from /fleet
  const [selfMarkWarn, setSelfMarkWarn] = useState(null);       // {distance} | null
  const mapCenterRef = useRef(null);

  // Location sharing by session ID.
  const [shareLocation, setShareLocation] = useState(!!SAVED_SETTINGS.shareLocation);
  const [watchId, setWatchId] = useState("");
  const [watchedLoc, setWatchedLoc] = useState(null); // {lat,lng} of the ID we're watching

  // Proximity takeover event + crowd cull-vote.
  const [proximityEvent, setProximityEvent] = useState(null); // {code, id}
  const [proximitySignal, setProximitySignal] = useState(1);  // 0..1 link strength
  const proximityCooldownRef = useRef(0);
  const [cullVote, setCullVote] = useState(null);
  const [cullVotedId, setCullVotedId] = useState(null);
  const cullVoteKeyRef = useRef(0);
  const cullVoteRef = useRef(null);          // latest vote, for the camera tour
  const tourPauseRef = useRef(0);            // tour paused until this timestamp
  const [voteSpotlight, setVoteSpotlight] = useState(null); // name shown on screen

  // First visit gets the briefing once the loader clears; after that it lives
  // behind the (?) in the top bar. Keyed separately from the settings blob so
  // clearing settings doesn't make it reappear for a regular.
  const finishLoading = useCallback(() => {
    setBootPhase("live");
    try {
      if (!localStorage.getItem("tracker_briefed")) {
        localStorage.setItem("tracker_briefed", "1");
        setBriefingOpen(true);
      }
    } catch { /* private browsing — they just don't get the auto-open */ }
  }, []);

  // Pan back to your own position. Once you've dragged away from yourself there
  // was no way back short of a reload — the map has no zoom control and no
  // locate button, and following an asset only ever centres the asset.
  const recenterOnMe = useCallback(() => {
    if (!userLocation) { toast.error("No location fix yet"); return; }
    setFollow(false);
    // Outside the operating area there is nothing to see where you are, so the
    // camera goes back to the observation area rather than to a blank ocean.
    if (inOperatingArea(userLocation)) {
      setFlyTo({ lat: userLocation.lat, lng: userLocation.lng, zoom: 15 });
    } else {
      setFlyTo({ lat: DEFAULT_LAT, lng: DEFAULT_LNG, zoom: 9 });
    }
  }, [userLocation]);

  // Stable callbacks so the memoized side panels don't re-render on every
  // animation frame (reading live data from refs keeps their identity fixed).
  const toggleTracked = useCallback(() => setTrackedOpen((o) => !o), []);
  const toggleActivity = useCallback(() => setActivityOpen((o) => !o), []);
  const handleSelect = useCallback((id) => {
    if (!id) { setSelectedId(null); setFollow(false); return; }
    setSelectedId(id);
    setFollow(true);           // clicking an asset camera-tracks it
    const a = agentsRef.current.find((x) => x.id === id);
    const tw = tweensRef.current[id];
    const pos = (tw && tw.current) || (a && a.location);
    if (pos) setFlyTo({ ...pos });
  }, []);

  // --- Operator console handlers ---
  const handleLogoTap = useCallback(() => {
    const s = logoTapsRef.current;
    const now = Date.now();
    s.n = now - s.t < 1600 ? s.n + 1 : 1;
    s.t = now;
    if (s.n >= 8) { s.n = 0; setAdminPrompt(true); setAdminPw(""); }
  }, []);

  const submitAdminPw = useCallback((e) => {
    e.preventDefault();
    if (adminPw.trim().toUpperCase() === "SPECTRE") {
      setAdminPrompt(false);
      setAdminOpen(true);
      toast("OPERATOR ACCESS GRANTED", { duration: 2500 });
    } else {
      setAdminPrompt(false);
      toast("ACCESS DENIED", { duration: 2000 });
    }
  }, [adminPw]);

  const adminCall = useCallback(async (action, body) => {
    try {
      const res = await axios.post(`${API}/admin/${action}`, { key: "SPECTRE", ...body });
      toast(res.data.message ? res.data.message.toUpperCase() : "DONE", { duration: 2500 });
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.detail || "COMMAND FAILED";
      toast(String(msg).toUpperCase(), { duration: 2500 });
      return null;
    }
  }, []);

  // Operator trace editing — the live trace list drives both the console list
  // and the deletable map markers while the console is open.
  const refreshAdminTraces = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/traces`);
      setAdminTraces(r.data.traces || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (!adminOpen) return undefined;
    refreshAdminTraces();
    const id = setInterval(refreshAdminTraces, 4000);
    return () => clearInterval(id);
  }, [adminOpen, refreshAdminTraces]);

  const deleteAdminTrace = useCallback(async (traceId) => {
    setAdminTraces((prev) => prev.filter((t) => t.id !== traceId));  // optimistic
    await adminCall("trace-del", { trace_id: traceId });
    refreshAdminTraces();
  }, [adminCall, refreshAdminTraces]);

  const handleAdminMapClick = useCallback((latlng) => {
    if (!adminArmed) return;
    if (adminArmed.mode === "trace-add") {
      adminCall("trace-add", { lat: latlng.lat, lng: latlng.lng }).then(refreshAdminTraces);
      return;   // stay armed so several traces can be dropped in a row
    }
    adminCall(adminArmed.mode, { agent_id: adminArmed.agentId, lat: latlng.lat, lng: latlng.lng });
    setAdminArmed(null);
  }, [adminArmed, adminCall, refreshAdminTraces]);

  const handleAdminHalt = useCallback((agentId) => adminCall("halt", { agent_id: agentId }), [adminCall]);
  const handleAdminBattery = useCallback((agentId, level) => adminCall("battery", { agent_id: agentId, level }), [adminCall]);
  const handleAdminState = useCallback((agentId, state) => adminCall("state", { agent_id: agentId, state }), [adminCall]);

  // ESC cancels an armed map-pick; also closes the password prompt.
  useEffect(() => {
    if (!adminArmed && !adminPrompt) return undefined;
    const h = (e) => { if (e.key === "Escape") { setAdminArmed(null); setAdminPrompt(false); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [adminArmed, adminPrompt]);

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        SETTINGS_KEY,
        JSON.stringify({ privacyMode, soundEnabled, shareLocation }),
      );
    } catch { /* private browsing / quota — settings just won't persist */ }
  }, [privacyMode, soundEnabled, shareLocation]);

  const playBlip = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(740, t);
      osc.frequency.exponentialRampToValueAtTime(1200, t + 0.08);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.12, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(t); osc.stop(t + 0.3);
    } catch {}
  }, [soundEnabled]);

  // Muffled "mehr-mehr" telephone mutter for phone-booth arrivals. A finite
  // burst of 4-6 synthesized syllables (~0.5-0.9s) through a telephone-band
  // filter — nothing here loops, and the caller enforces a global cooldown.
  const playPhoneTalk = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      let t = ctx.currentTime + 0.05;
      const band = ctx.createBiquadFilter();
      band.type = "bandpass";
      band.frequency.value = 900;      // classic telephone voice band
      band.Q.value = 1.2;
      const master = ctx.createGain();
      master.gain.value = 0.045;       // very quiet — heard, not announced
      band.connect(master);
      master.connect(ctx.destination);
      const syllables = 4 + Math.floor(Math.random() * 3);
      for (let i = 0; i < syllables; i++) {
        const dur = 0.07 + Math.random() * 0.08;
        const osc = ctx.createOscillator();
        osc.type = "sawtooth";
        const f0 = 120 + Math.random() * 60;
        osc.frequency.setValueAtTime(f0, t);
        osc.frequency.exponentialRampToValueAtTime(
          f0 * (0.85 + Math.random() * 0.35), t + dur
        );
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(1, t + 0.015);
        g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        osc.connect(g);
        g.connect(band);
        osc.start(t);
        osc.stop(t + dur + 0.02);
        t += dur + 0.02 + Math.random() * 0.05;
      }
    } catch {}
  }, [soundEnabled]);

  // Shorter, higher-pitched click for incoming emotes — distinct from the
  // agent-handoff blip so users learn the difference.
  const playEmoteBlip = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(1600, t);
      osc.frequency.exponentialRampToValueAtTime(2000, t + 0.05);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.06, t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(t); osc.stop(t + 0.15);
    } catch {}
  }, [soundEnabled]);

  const fetchState = useCallback(async () => {
    try {
      const [fRes, logRes, emRes, trRes] = await Promise.all([
        // sid is only a fallback identity for marking limits when the server
        // can't see an IP (local/direct requests); IP wins when present.
        axios.get(`${API}/fleet`, { params: { sid: WATCHER_ID } }),
        axios.get(`${API}/activity-log?limit=10`),
        axios.get(`${API}/emotes`),
        axios.get(`${API}/traces`),
      ]);
      const list = fRes.data.agents || [];
      setAgents(list);
      agentsRef.current = list;
      setServerTime(fRes.data.server_timestamp);
      setWatchersOnline(fRes.data.watchers_online ?? 0);
      setActivities(logRes.data.activities || []);
      setTraces(trRes.data.traces || []);

      // Marking spots (server state, so every viewer sees the same set). Toast
      // once for each newly-seen stranger mark — that's the creepy beat.
      const marks = fRes.data.marks || [];
      marks.forEach((mk) => {
        if (mk.by_stranger && !seenMarkIds.current.has(mk.id)) {
          seenMarkIds.current.add(mk.id);
          toast(`An unknown signal marked a spot near ${mk.region}`, { duration: 4500 });
        } else {
          seenMarkIds.current.add(mk.id);
        }
      });
      setMarkSpots(marks);

      // Crowd cull-vote (shared). Each ROUND is a fresh ballot, so clear our
      // local "voted" state whenever the round changes.
      const cv = fRes.data.cull_vote || null;
      if (cv && cv.deadline_ts && cv.deadline_ts !== cullVoteKeyRef.current) {
        cullVoteKeyRef.current = cv.deadline_ts;
        setCullVotedId(null);
      }
      cullVoteRef.current = cv;
      setCullVote(cv);

      // Marking window/eligibility, computed server-side for our IP/session.
      if (fRes.data.mark_state) {
        setMarkServerState({ ...fRes.data.mark_state, _at: Date.now() });
      }

      // Emote handling: toast + short chirp each newly-seen emote, then update state
      const incoming = emRes.data.emotes || [];
      if (emRes.data.ttl_seconds > 0) emoteTtlMsRef.current = emRes.data.ttl_seconds * 1000;
      let newCount = 0;
      incoming.forEach((e) => {
        if (!seenEmoteIds.current.has(e.id)) {
          seenEmoteIds.current.add(e.id);
          newCount += 1;
          const meta = getEmoteIcon(e.emoji_id);
          const label = meta ? meta.label : e.emoji_id;
          toast(`Someone in ${e.region} sent a ${label}`, { duration: 3500 });
        }
      });
      if (newCount > 0) playEmoteBlip();
      // Clock-skew-safe lifetimes: stamp each emote when FIRST seen on this
      // device and age it locally from that moment. Comparing the server's
      // expires_ts against the device clock made emotes vanish instantly (and
      // flicker back each poll) whenever the phone's clock ran a few seconds
      // ahead of the server.
      setEmotes((prev) => {
        const seenAt = new Map(prev.map((e) => [e.id, e._seenAt]));
        return incoming.map((e) => ({ ...e, _seenAt: seenAt.get(e.id) || Date.now() }));
      });

      // Per-agent motion: follow the actual route polyline (road-hugging).
      // Anti-snap: never hard-jump to the server position. While moving we
      // keep continuity from the currently DISPLAYED point and converge a
      // fraction of the error each poll; on stop we ease into the resting
      // point. Only a genuine relocation (> SNAP_KM, e.g. an overnight
      // fast-forward) teleports.
      const SNAP_KM = 1.0;
      const CONVERGE = 0.35; // fraction of drift corrected per poll
      const tweens = tweensRef.current;
      const seenIds = new Set();
      list.forEach((a) => {
        seenIds.add(a.id);
        const route = a.route && a.route.length > 1 ? a.route : null;
        const prev = tweens[a.id];
        const prevPos = prev && prev.current;
        const gapKm = prevPos ? haversineKm(prevPos, a.location) : Infinity;
        if (route && a.status === "moving") {
          const serverKm = nearestDistanceAlong(route, a.location);
          let startKm = serverKm;
          if (prevPos && gapKm < SNAP_KM) {
            const curKm = nearestDistanceAlong(route, prevPos);
            startKm = curKm + (serverKm - curKm) * CONVERGE;
            // Assets never drive backwards: if the client got ahead, hold and
            // let the server catch up rather than reversing down the road.
            if (startKm < curKm) startKm = curKm;
          }
          tweens[a.id] = {
            mode: "route",
            route,
            routeLen: polylineLength(route),
            startKm,
            speedKmh: a.speed || 0,
            startAt: Date.now(),
            current: pointAtDistance(route, startKm),
          };
        } else if (prevPos && gapKm < SNAP_KM && gapKm > 1e-6) {
          // Stopped: glide the last displayed position into the resting point.
          tweens[a.id] = {
            mode: "settle",
            from: prevPos,
            to: a.location,
            startAt: Date.now(),
            durationMs: 900,
            current: prevPos,
          };
        } else {
          tweens[a.id] = { mode: "point", current: a.location };
        }
      });
      Object.keys(tweens).forEach((id) => { if (!seenIds.has(id)) delete tweens[id]; });

      // Chirp when a brand-new call-sign appears (skip the very first load)
      if (prevIdsRef.current.size > 0 && list.some((a) => !prevIdsRef.current.has(a.id))) {
        playBlip();
      }
      prevIdsRef.current = seenIds;

      // Phone-booth arrival: an asset that WAS heading to a phone booth and
      // is now idle just picked up the receiver. One muffled mutter, at most
      // every 25s globally, no matter how many arrive at once.
      const prevMap = prevAssetsRef.current;
      const phoneArrival = list.some((a) => {
        const prev = prevMap[a.id];
        return prev && prev.status === "moving" &&
               prev.destType === "phone_booth" && a.status === "idle";
      });
      prevAssetsRef.current = Object.fromEntries(
        list.map((a) => [a.id, { status: a.status, destType: a.destination_type }])
      );
      if (phoneArrival && Date.now() - lastPhoneTalkRef.current > 25000) {
        lastPhoneTalkRef.current = Date.now();
        playPhoneTalk();
      }
    } catch (e) { /* non-fatal */ }
  }, [playBlip, playEmoteBlip, playPhoneTalk]);

  const sendEmote = useCallback(async (emoteId) => {
    try {
      const body = {
        watcher_id: WATCHER_ID,
        emoji_id: emoteId,
        ...(userLocation && !privacyMode
          ? { lat: userLocation.lat, lng: userLocation.lng }
          : {}),
      };
      const res = await axios.post(`${API}/emote`, body);
      // Optimistically push our own emote into local state so we see it instantly
      if (res.data && res.data.success && res.data.emote) {
        const e = res.data.emote;
        seenEmoteIds.current.add(e.id);  // suppress duplicate toast on next poll
        setEmotes((prev) => [{ ...e, _seenAt: Date.now() }, ...prev]);
        // Make sure the sender can see their own emote — fly the map to it.
        // (without this, a panned/zoomed/following map can leave it off-screen.)
        setFlyTo({ lat: e.lat, lng: e.lng });
      }
      return res.data;
    } catch (err) {
      return { success: false, reason: "network" };
    }
  }, [userLocation, privacyMode]);

  // Marking-window UI state: server-authoritative, with the countdowns ticked
  // down locally between polls so the label counts smoothly.
  const markState = useMemo(() => {
    // Window phase always from the live clock, so the countdown can never stall.
    const ph = markWindowPhase(now.getTime());
    const s = markServerState;
    if (!s) {
      return { canMark: false, label: ph.open ? "SYNCING…" : `NEXT WINDOW · ${fmtMS(ph.opensIn)}` };
    }
    // "Already marked" only applies to the window it was recorded in — so it
    // clears itself when the next window opens even before the next poll.
    if (s.already_marked && s.window_id === ph.windowId) {
      return { canMark: false, label: `MARKED · NEXT ${fmtMS(ph.opensIn || MARK_PERIOD_S)}` };
    }
    if (!ph.open) return { canMark: false, label: `NEXT WINDOW · ${fmtMS(ph.opensIn)}` };
    const elapsed = Math.max(0, (now.getTime() - s._at) / 1000);
    const eligibleIn = Math.max(0, (s.eligible_in || 0) - elapsed);
    if (!s.eligible && eligibleIn > 0) {
      return { canMark: false, label: `WARMING UP · ${fmtMS(eligibleIn)}` };
    }
    return { canMark: true, label: `MARK THIS SPOT · ${Math.ceil(ph.closesIn)}s` };
  }, [now, markServerState]);

  // Drop a marking spot at the crosshair (map centre). Gated by the synchronized
  // window; the server enforces window + eligibility authoritatively.
  const handleMark = useCallback(async (opts = {}) => {
    if (!markState.canMark) { toast.error(markState.label.replace(/·.*/, "").trim() || "Not available"); return { success: false }; }
    const center = mapCenterRef.current;
    if (!center) { toast.error("Map not ready"); return { success: false }; }
    // Marking on top of yourself broadcasts where you are to every viewer —
    // make that an explicit, deliberate choice.
    if (!opts.confirmedSelf && userLocation && !privacyMode) {
      const d = metersBetween(userLocation, center);
      if (d < SELF_MARK_WARN_M) {
        setSelfMarkWarn({ distance: Math.round(d) });
        return { success: false, reason: "needs_confirm" };
      }
    }
    try {
      const res = await axios.post(`${API}/mark`, { watcher_id: WATCHER_ID, lat: center.lat, lng: center.lng });
      if (res.data && res.data.success) {
        const mk = res.data.mark;
        seenMarkIds.current.add(mk.id);
        setMarkSpots((prev) => [mk, ...prev.filter((m) => m.id !== mk.id)]);
        // Lock the button immediately; the next poll confirms from the server.
        setMarkServerState((s) => (s ? { ...s, already_marked: true, can_mark: false } : s));
        setFlyTo({ lat: mk.lat, lng: mk.lng });
        toast.success("Spot marked", { duration: 2500 });
      } else if (res.data) {
        const r = res.data.reason;
        if (r === "window_closed") toast.error(`Marking opens in ${fmtMS(res.data.opens_in || 0)}`);
        else if (r === "not_eligible") toast.error(`Not eligible yet · ${fmtMS(res.data.eligible_in || 0)} of viewing left`);
        else if (r === "already_marked") {
          setMarkServerState((s) => (s ? { ...s, already_marked: true, can_mark: false } : s));
          toast.error("Already marked this window");
        } else toast.error("Couldn't mark that spot");
      }
      return res.data;
    } catch {
      return { success: false, reason: "network" };
    }
  }, [markState, userLocation, privacyMode]);

  const updateWatcherBackend = useCallback(async (lat, lng) => {
    if (privacyMode) return;
    try {
      await axios.post(`${API}/update-user-location`, { lat, lng, watcher_id: WATCHER_ID, share: shareLocation });
    } catch {}
  }, [privacyMode, shareLocation]);

  // Poll the location of a session ID we're watching (only resolves if that
  // person has sharing switched on).
  useEffect(() => {
    const code = (watchId || "").trim().toUpperCase();
    if (code.length < 4) { setWatchedLoc(null); return undefined; }
    let alive = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/shared-location/${code}`);
        if (alive) setWatchedLoc(res.data && res.data.found ? { lat: res.data.lat, lng: res.data.lng } : null);
      } catch { if (alive) setWatchedLoc(null); }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [watchId]);

  // Two-tone "connection request" chime for the proximity takeover.
  const playChime = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const t = ctx.currentTime;
      [[660, 0], [880, 0.16], [1180, 0.34]].forEach(([f, dt]) => {
        const osc = ctx.createOscillator(); const g = ctx.createGain();
        osc.type = "sine"; osc.frequency.setValueAtTime(f, t + dt);
        g.gain.setValueAtTime(0.0001, t + dt);
        g.gain.exponentialRampToValueAtTime(0.08, t + dt + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + dt + 0.22);
        osc.connect(g); g.connect(ctx.destination);
        osc.start(t + dt); osc.stop(t + dt + 0.24);
      });
    } catch { /* ignore */ }
  }, [soundEnabled]);

  // Proximity detection: fire the takeover when a moving asset gets close.
  useEffect(() => {
    if (privacyMode || !userLocation || proximityEvent) return undefined;
    const id = setInterval(() => {
      if (Date.now() < proximityCooldownRef.current) return;
      let near = null, nearD = Infinity;
      for (const a of agentsRef.current) {
        if (a.status !== "moving") continue;
        const tw = tweensRef.current[a.id];
        const loc = (tw && tw.current) || a.location;
        if (!loc) continue;
        const d = metersBetween(userLocation, loc);
        if (d < nearD) { nearD = d; near = a; }
      }
      if (near && nearD < PROXIMITY_M && Math.random() < 0.65) {
        setProximityEvent({ code: near.code, id: near.id });
        setFlyTo({ lat: userLocation.lat, lng: userLocation.lng, zoom: 17 });
      }
    }, 1400);
    return () => clearInterval(id);
  }, [privacyMode, userLocation, proximityEvent]);

  // Link-strength driver: the red bar tracks the asset's distance while it's
  // near, and drains ultra-fast if it goes offline/vanishes. Empty bar = the
  // asset is out of range and your view is released.
  useEffect(() => {
    if (!proximityEvent) { setProximitySignal(1); return undefined; }
    const startedAt = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      if (elapsed > PROXIMITY_MAX_MS) { setProximityEvent(null); return; }
      if (elapsed < PROXIMITY_LOCK_MS) { setProximitySignal(1); return; } // intro plays at full
      const veh = agentsRef.current.find((a) => a.id === proximityEvent.id);
      const tw = veh && tweensRef.current[veh.id];
      const loc = (tw && tw.current) || (veh && veh.location);
      // Presence, NOT movement. An asset that has ARRIVED goes status:"idle" —
      // the drone lands and sits there. Treating that as offline crashed the
      // link bar and released the view while the asset was still parked next to
      // you. Only a genuinely vanished / signal-lost asset drains fast.
      const gone = !veh || !loc || !userLocation || veh.signal_searching;
      setProximitySignal((prev) => {
        let next;
        if (gone) {
          next = prev - 0.16;                 // ultra-rapid drain (~1s to empty)
        } else {
          const d = metersBetween(userLocation, loc);
          const target = Math.max(0, Math.min(1, 1 - (d - PROXIMITY_M) / PROXIMITY_DECAY_RANGE));
          next = prev + (target - prev) * 0.35; // ease toward distance-based strength
        }
        return Math.max(0, Math.min(1, next));
      });
    }, 200);
    return () => clearInterval(id);
  }, [proximityEvent, userLocation]);

  // Bar emptied -> connection lost, release the view.
  useEffect(() => {
    if (proximityEvent && proximitySignal <= 0.015) {
      setProximityEvent(null);
      toast("Signal lost — view restored", { duration: 2500 });
    }
  }, [proximityEvent, proximitySignal]);

  // Arm the cooldown when a takeover ENDS, not when it starts — and do it here
  // so every exit is covered (link drained, failsafe timeout, visibility
  // disabled). Starting the clock at trigger time meant a takeover that ran its
  // full PROXIMITY_MAX_MS had already burned the entire cooldown, so a second
  // full-screen override could seize the map a second after the first released.
  const hadProximityRef = useRef(false);
  useEffect(() => {
    if (proximityEvent) { hadProximityRef.current = true; return; }
    if (hadProximityRef.current) {
      hadProximityRef.current = false;
      proximityCooldownRef.current = Date.now() + PROXIMITY_COOLDOWN_MS;
    }
  }, [proximityEvent]);

  const handleProximityDisable = useCallback(() => {
    setPrivacyMode(true);   // real effect: stop broadcasting your location
    setProximityEvent(null);
    toast("Visibility disabled — you're off their radar", { duration: 3000 });
  }, []);

  // Deep links. `?watch=ABC123` prefills the watch field and `?asset=X544`
  // opens tracking on a call-sign, so "here's my ID" and "look at this one"
  // become a link instead of a set of instructions. The asset code is held
  // until the fleet arrives, since the first poll hasn't landed on mount.
  const [pendingAssetCode, setPendingAssetCode] = useState(null);
  useEffect(() => {
    let params;
    try { params = new URLSearchParams(window.location.search); }
    catch { return; }
    const watch = (params.get("watch") || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (watch.length >= 4) {
      setWatchId(watch.slice(0, 6));
      setShowSettings(true);      // show them what the link just did
    }
    const asset = (params.get("asset") || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (asset) setPendingAssetCode(asset);
  }, []);

  // Resolve ?asset= once a fleet is on screen. Codes are recycled at phone
  // booths, so a stale link simply finds nothing — say so rather than sitting
  // silent, and give up either way so this can't retry on every poll.
  useEffect(() => {
    if (!pendingAssetCode || agents.length === 0) return;
    const match = agents.find((a) => (a.code || "").toUpperCase() === pendingAssetCode);
    if (match) handleSelect(match.id);
    else toast(`${pendingAssetCode} is not broadcasting`, { duration: 3500 });
    setPendingAssetCode(null);
  }, [pendingAssetCode, agents, handleSelect]);

  const [linkCopied, setLinkCopied] = useState(false);
  const [idCopied, setIdCopied] = useState(false);
  const copySessionId = useCallback(() => {
    copyText(sessionId)
      .then(() => { setIdCopied(true); setTimeout(() => setIdCopied(false), 1500); })
      .catch(() => toast(`Your ID is ${sessionId}`));
  }, [sessionId]);

  // A link that lands on the map with the watch field already filled in.
  const copyWatchLink = useCallback(() => {
    const url = `${window.location.origin}${window.location.pathname}?watch=${sessionId}`;
    copyText(url)
      .then(() => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 1800); })
      .catch(() => toast(url));
  }, [sessionId]);

  // During an open round the camera slowly tours the marks up for the vote,
  // naming each one, so people can see what they're voting on. Keyed on the
  // round so it restarts per ballot (not on every 2s poll).
  const voteRound = cullVote && !cullVote.resolved && !cullVote.finished ? cullVote.round : 0;
  useEffect(() => {
    if (!voteRound) { setVoteSpotlight(null); return undefined; }
    let i = 0;
    const show = () => {
      if (Date.now() < tourPauseRef.current) return;   // user is looking at one
      const cv = cullVoteRef.current;
      const opts = (cv && cv.options) || [];
      if (!opts.length) return;
      const o = opts[i % opts.length];
      i += 1;
      if (o) { setVoteSpotlight(o.name); setFlyTo({ lat: o.lat, lng: o.lng, zoom: 15 }); }
    };
    show();
    const id = setInterval(show, 3400);
    return () => clearInterval(id);
  }, [voteRound]);

  const handleCullVote = useCallback(async (markId) => {
    if (cullVotedId) return;
    setCullVotedId(markId); // optimistic
    try { await axios.post(`${API}/mark/vote`, { watcher_id: WATCHER_ID, mark_id: markId }); }
    catch { /* ignore — server is authoritative on the next poll */ }
  }, [cullVotedId]);

  // Tapping a vote option's NAME flies the map to that marker. Pauses the
  // automatic tour for a bit so the camera doesn't yank away immediately.
  const handleCullLocate = useCallback((opt) => {
    if (!opt) return;
    tourPauseRef.current = Date.now() + 9000;
    setVoteSpotlight(opt.name);
    setFlyTo({ lat: opt.lat, lng: opt.lng, zoom: 15 });
  }, []);

  useEffect(() => {
    if (!("geolocation" in navigator)) { setUserLocation({ lat: DEFAULT_LAT, lng: DEFAULT_LNG }); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
      () => { setUserLocation({ lat: DEFAULT_LAT, lng: DEFAULT_LNG }); },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
    const id = navigator.geolocation.watchPosition(
      (pos) => { setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
      null,
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // Push our position to the server when it has meaningfully changed. A
  // high-accuracy watchPosition fires roughly once a second while walking, and
  // this used to POST on every single one of those — a request per second per
  // phone, for a server that prunes watchers after 60 s and only needs a rough
  // fix. Gate on distance moved or elapsed time, whichever comes first; the
  // 30 s keep-alive interval below covers standing still.
  const lastSentLocRef = useRef(null);
  useEffect(() => {
    if (!userLocation) return;
    const last = lastSentLocRef.current;
    const movedEnough = !last || metersBetween(last, userLocation) > 25;
    const staleEnough = !last || Date.now() - last.at > 15000;
    if (!movedEnough && !staleEnough) return;
    lastSentLocRef.current = { ...userLocation, at: Date.now() };
    updateWatcherBackend(userLocation.lat, userLocation.lng);
  }, [userLocation, updateWatcherBackend]);

  useEffect(() => {
    if (privacyMode || !userLocation) return;
    const id = setInterval(() => updateWatcherBackend(userLocation.lat, userLocation.lng), 30000);
    return () => clearInterval(id);
  }, [privacyMode, userLocation, updateWatcherBackend]);

  useEffect(() => {
    (async () => {
      try {
        if (privacyMode) await axios.post(`${API}/clear-user-location`, { lat: 0, lng: 0, watcher_id: WATCHER_ID });
        else if (userLocation) updateWatcherBackend(userLocation.lat, userLocation.lng);
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privacyMode]);

  useEffect(() => {
    const tick = setInterval(() => {
      // Hold positions steady during a zoom so Leaflet animates markers with
      // the tiles instead of us snapping them to the target-zoom pixel.
      if (zoomingRef.current) return;
      const tweens = tweensRef.current;
      const ids = Object.keys(tweens);
      if (ids.length === 0) return;
      const next = {};
      let moved = false;
      ids.forEach((id) => {
        const tw = tweens[id];
        if (!tw) return;
        let loc;
        if (tw.mode === "route") {
          const elapsedS = (Date.now() - tw.startAt) / 1000;
          const km = Math.min(tw.routeLen, tw.startKm + (tw.speedKmh * elapsedS) / 3600);
          loc = pointAtDistance(tw.route, km);
        } else if (tw.mode === "settle") {
          const t = Math.min(1, (Date.now() - tw.startAt) / tw.durationMs);
          const e = 1 - (1 - t) * (1 - t);
          loc = {
            lat: tw.from.lat + e * (tw.to.lat - tw.from.lat),
            lng: tw.from.lng + e * (tw.to.lng - tw.from.lng),
          };
        } else {
          loc = tw.current;
        }
        const prev = tw.current;
        if (!prev || Math.abs(prev.lat - loc.lat) > 1e-7 || Math.abs(prev.lng - loc.lng) > 1e-7) {
          moved = true;
        }
        tw.current = loc;
        next[id] = loc;
      });
      // Skip the re-render entirely when the whole fleet is stationary.
      if (moved) setDisplayLocations(next);
      // Phones do a full marker re-render per tick; 20 Hz of that competes with
      // touch gestures, so halve the rate there. Motion still reads as smooth
      // because Leaflet interpolates the marker transform.
    }, L.Browser.mobile ? 100 : 50);
    return () => clearInterval(tick);
  }, []);

  // Everything happens in Queensland. A viewer somewhere else used to get the
  // map centred on themselves and then dragged back by maxBounds, with every
  // asset filtered out for being thousands of km beyond its signal radius —
  // an empty grey rectangle and no explanation. They're put in the observation
  // area instead, and told why.
  const outOfRegion = !!userLocation && !inOperatingArea(userLocation);
  const mapCenter = userLocation && !outOfRegion
    ? [userLocation.lat, userLocation.lng]
    : [DEFAULT_LAT, DEFAULT_LNG];

  useEffect(() => {
    fetchState();
    const id = setInterval(fetchState, 2000);
    return () => clearInterval(id);
  }, [fetchState]);

  // Client-side emote expiry — drop emotes whose window has passed even between
  // polls. Runs at 4 Hz so the marker disappears promptly.
  //
  // The lifetime comes from the server's `ttl_seconds`, aged from when this
  // device FIRST saw the emote (clock-skew-safe, same reason `_seenAt` exists).
  // This used to compute the span as `expires_ts - created_ts`, but `created_ts`
  // is not on the wire — so the subtraction yielded a raw unix timestamp, the
  // TTL came out around 54 years, and this sweep never expired anything.
  useEffect(() => {
    if (emotes.length === 0) return undefined;
    const id = setInterval(() => {
      const now = Date.now();
      const ttlMs = emoteTtlMsRef.current;
      setEmotes((prev) => prev.filter((e) => now - (e._seenAt || now) < ttlMs));
    }, 250);
    return () => clearInterval(id);
  }, [emotes.length]);

  // Keyboard shortcuts. Skipped while typing (the watch-ID and operator
  // password fields), while a modifier is held, and while the operator console
  // owns ESC for cancelling an armed map-pick.
  useEffect(() => {
    const isTyping = (t) =>
      !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      switch (e.key.toLowerCase()) {
        case "escape":
          if (adminPrompt || adminArmed) return;   // handled by the console
          if (briefingOpen) return;                // the panel closes itself
          if (traceScreenOpen) setTraceScreenOpen(false);
          else if (showSettings) setShowSettings(false);
          else if (selectedId) handleSelect(null);
          else return;
          break;
        case "?": setBriefingOpen(true); break;
        case "f":
          if (!selectedId) return;
          setFollow((v) => !v);
          break;
        case "a": toggleActivity(); break;
        case "t": setTraceScreenOpen((v) => !v); break;
        case "c": recenterOnMe(); break;
        case "m": handleMark(); break;
        default: return;
      }
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    adminPrompt, adminArmed, briefingOpen, traceScreenOpen, showSettings,
    selectedId, handleSelect, toggleActivity, recenterOnMe, handleMark,
  ]);

  // Fly to where an activity event happened. The server has always sent a
  // `location` with each log line; nothing used it, so the feed was read-only
  // text next to a map that could have shown the spot.
  const locateActivity = useCallback((loc) => {
    if (!loc || typeof loc.lat !== "number") return;
    setFollow(false);
    setFlyTo({ lat: loc.lat, lng: loc.lng, zoom: 15 });
  }, []);

  const showLoader = bootPhase === "loading";
  const selected = agents.find((a) => a.id === selectedId) || null;
  const followLocation = follow && selected
    ? (displayLocations[selected.id] || selected.location)
    : null;

  // An asset only paints when the viewer is inside its (server-synced) signal
  // radius — usually ~30-40 km, wobbling a little. Without a GPS fix we can't
  // measure distance, so nothing is hidden. Nor is anything hidden from a
  // viewer outside the operating area: the radius rule exists to make proximity
  // matter locally, and for someone watching from another continent it would
  // only ever mean an empty map.
  const withinSignal = (a) => {
    if (!userLocation || outOfRegion) return true;
    const loc = displayLocations[a.id] || a.location;
    if (!loc) return false;
    return haversineKm(userLocation, loc) <= (a.visibility_radius_km || 40);
  };

  return (
    <div className={`app-container${adminArmed ? " admin-armed" : ""}${proximityEvent ? " signal-scramble" : ""}`} data-testid="live-ui">
      <Toaster position="top-center" theme="dark" />

      {/* You've hidden yourself — clear way back to visible. */}
      {privacyMode && !proximityEvent && (
        <button
          className="visibility-pill"
          onClick={() => setPrivacyMode(false)}
          data-testid="visibility-pill"
          title="You're hidden from assets — tap to become visible again"
        >
          <EyeOff size={12} /> HIDDEN · TAP TO REVEAL
        </button>
      )}

      {/* You're watching from outside the operating area. Say so once, rather
          than presenting an empty map as if it were the state of the world. */}
      {outOfRegion && !regionNoticeDismissed && bootPhase === "live" && (
        <div className="region-notice" data-testid="region-notice">
          <span className="region-notice-body">
            <strong>REMOTE OBSERVATION</strong>
            You're outside the operating area. All assets are in Queensland,
            Australia — signal range and proximity don't apply from here, so
            everything on the move is shown.
          </span>
          <button
            className="region-notice-x"
            onClick={() => setRegionNoticeDismissed(true)}
            aria-label="Dismiss"
            data-testid="region-notice-dismiss"
          >
            <X size={13} />
          </button>
        </div>
      )}

      {/* Subtle ghostly ripple/scan overlay (GPU transforms only) */}
      <div className="ghost-overlay" aria-hidden="true">
        <div className="ghost-ripple" />
        <div className="ghost-scan" />
      </div>

      <div className="top-bar">
        <div className="top-bar-left">
          <div className="logo" onClick={handleLogoTap}>
            <Radio size={14} />
            <span>TRACKER</span>
          </div>
          {/* The zone suffix and the "ONLINE" label below are in spans purely
              so the top bar can shed them on a narrow screen — see the
              overflow-priority rules in index.css. */}
          <div className="time-display" data-testid="time-display">
            {formatTime(now.toISOString())}<span className="time-zone"> AEST</span>
          </div>
        </div>
        <div className="top-bar-right">
          <div className="watchers-badge" data-testid="watchers-online">
            <Signal size={12} /> {watchersOnline}<span className="watchers-label"> ONLINE</span>
          </div>
          <div className="session-badge" data-testid="session-id" title="Your session ID — share it to let someone track you">
            <span>ID {sessionId}</span>
            <button className="session-copy" onClick={copySessionId} title="Copy your ID" data-testid="copy-id">
              {idCopied ? <Check size={11} /> : <Copy size={11} />}
            </button>
          </div>
          <button
            className="settings-btn help-btn"
            onClick={() => setBriefingOpen(true)}
            data-testid="briefing-btn"
            title="What am I looking at?"
            aria-label="What am I looking at?"
          >
            <HelpCircle size={14} />
          </button>
          <button className="settings-btn" onClick={() => setShowSettings(!showSettings)} data-testid="settings-btn">
            <Settings size={14} />
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="settings-panel" data-testid="settings-panel">
          <div className="settings-header">
            <span>SETTINGS</span>
            <button onClick={() => setShowSettings(false)}><X size={12} /></button>
          </div>
          <div className="settings-option">
            <div className="option-row">
              <EyeOff size={16} />
              <span>Privacy Mode</span>
              <button className="info-btn" onClick={() => setShowPrivacyInfo(!showPrivacyInfo)}><Info size={12} /></button>
            </div>
            <label className="toggle-switch">
              <input type="checkbox" checked={privacyMode} onChange={(e) => setPrivacyMode(e.target.checked)} data-testid="privacy-toggle" />
              <span className="toggle-slider"></span>
            </label>
          </div>
          {showPrivacyInfo && (
            <div className="privacy-info">
              Hides your location on the map and stops sharing your coordinates. Agents will no longer react to your presence.
            </div>
          )}
          <div className="settings-option">
            <div className="option-row">
              {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
              <span>Signal Alerts</span>
            </div>
            <label className="toggle-switch">
              <input type="checkbox" checked={soundEnabled} onChange={(e) => setSoundEnabled(e.target.checked)} data-testid="sound-toggle" />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="settings-option">
            <div className="option-row">
              <Share2 size={16} />
              <span>Share my location</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={shareLocation}
                disabled={privacyMode}
                onChange={(e) => setShareLocation(e.target.checked)}
                data-testid="share-toggle"
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <div className="settings-sub">
            {shareLocation
              ? <>Anyone with your ID <strong>{sessionId}</strong> can see you.</>
              : <>Give someone your ID <strong>{sessionId}</strong> and toggle this on to let them watch.</>}
          </div>
          <button
            className="share-link-btn"
            onClick={copyWatchLink}
            data-testid="copy-watch-link"
            title="Copy a link that opens the map already watching you"
          >
            {linkCopied ? <Check size={11} /> : <Share2 size={11} />}
            {linkCopied ? "LINK COPIED" : "COPY WATCH LINK"}
          </button>

          <div className="settings-watch">
            <div className="option-row"><Eye size={16} /><span>Watch an ID</span></div>
            <input
              className="watch-input"
              value={watchId}
              maxLength={6}
              placeholder="ABC123"
              onChange={(e) => setWatchId(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              data-testid="watch-input"
            />
            <div className={`watch-status ${watchedLoc ? "live" : ""}`}>
              {(watchId || "").trim().length < 4 ? "" : watchedLoc ? "● LOCATED" : "no signal / not shared"}
            </div>
          </div>
        </div>
      )}

      <div className="map-container" data-testid="map-container">
        {/* Surveillance-console dressing: center crosshair + live coordinates */}
        <div className="map-crosshair" aria-hidden="true" />
        <div className="map-coords" ref={coordsRef} aria-hidden="true" />

        <button
          className="recenter-btn"
          onClick={recenterOnMe}
          data-testid="recenter-btn"
          title="Centre on my location (C)"
          aria-label="Centre the map on my location"
        >
          <LocateFixed size={14} />
        </button>
        <MapContainer
          center={mapCenter}
          zoom={13}
          minZoom={5}
          maxZoom={18}
          maxBounds={OPERATING_BOUNDS}
          maxBoundsViscosity={0.85}
          zoomAnimation={true}
          zoomSnap={1}
          scrollWheelZoom
          className="leaflet-dark"
          zoomControl={false}
        >
          {/* Leaflet's tile defaults are deliberately mobile-aware: updateWhenIdle
              is true on mobile so a pinch doesn't fire a tile request every frame,
              and keepBuffer 2 keeps the retained ring small. Forcing those made
              zooming on a phone crawl — pop-in is handled by the pre-mount tile
              warm instead. updateWhenZooming=false also skips mid-zoom reloads. */}
          <TileLayer
            attribution='&copy; OpenStreetMap contributors &copy; CARTO'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            updateWhenZooming={false}
            eventHandlers={{ load: () => setTilesReady(true) }}
          />
          <MapController
            flyTo={flyTo}
            followLocation={followLocation}
            zoomingRef={zoomingRef}
            coordsRef={coordsRef}
            centerRef={mapCenterRef}
            bootPhase={bootPhase}
            onMapClick={adminArmed ? handleAdminMapClick : null}
          />
          <EmoteEdgeIndicators emotes={emotes} />

          {/* Faint lat/lng graticule — one multi-polyline, non-interactive */}
          <Polyline
            positions={GRATICULE_POSITIONS}
            interactive={false}
            pathOptions={{ color: "#9fb6d4", opacity: 0.045, weight: 1 }}
          />

          {userLocation && !privacyMode && (
            <Marker position={[userLocation.lat, userLocation.lng]} icon={createUserIcon()}>
              <Popup><div className="popup-content"><strong>YOUR LOCATION</strong></div></Popup>
            </Marker>
          )}

          {/* Watched session ID (someone who shared their location with us). */}
          {watchedLoc && (
            <Marker position={[watchedLoc.lat, watchedLoc.lng]} icon={createMarkIcon(false, watchId.trim().toUpperCase())}>
              <Popup><div className="popup-content"><strong>ID {watchId.trim().toUpperCase()}</strong><span className="popup-sub">shared location</span></div></Popup>
            </Marker>
          )}

          {/* Marking spots — user-placed and the rare stranger ones. */}
          {markSpots.map((mk) => (
            <Marker key={`mk-${mk.id}`} position={[mk.lat, mk.lng]} icon={createMarkIcon(mk.by_stranger, mk.name || mk.label)}>
              <Popup>
                <MarkPopupBody mark={mk} />
              </Popup>
            </Marker>
          ))}

          {/* Moving vehicles, the tracked one, and ALWAYS the golden entity —
              it used to vanish whenever it parked, so launching toward a mark
              looked like it spawned there. Keeping it drawn makes its position
              continuous: you see it sitting somewhere, then set off. */}
          {agents.filter((a) => (a.status === "moving" && withinSignal(a)) || a.id === selectedId || a.id === "agent-goldcoast").map((a) => {
            const loc = displayLocations[a.id] || a.location;
            if (!loc) return null;
            if (a.signal_searching) {
              return (
                <Fragment key={`ag-${a.id}`}>
                  <Circle
                    center={[loc.lat, loc.lng]}
                    radius={500}
                    className="signal-searching-ring"
                    pathOptions={{
                      color: "#ff2d2d", weight: 1, opacity: 0.7,
                      fillColor: "#ff2d2d", fillOpacity: 0.07,
                    }}
                  />
                  {generateFootsteps(loc).map((f, i) => (
                    <Marker key={`fs-${a.id}-${i}`} position={[f.lat, f.lng]} icon={createFootstepIcon(i)}>
                      <Popup>
                        <div className="popup-content popup-searching">
                          <span className="popup-searching-msg">Footprints detected — possible pass-through</span>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </Fragment>
              );
            }
            return (
              <Marker
                key={`ag-${a.id}`}
                position={[loc.lat, loc.lng]}
                icon={createAgentIcon(a.id, a.code, a.status === "moving" && a.speed > 60, a.id === selectedId, a.id === "agent-goldcoast", !!a.drone)}
                eventHandlers={{ click: () => handleSelect(a.id) }}
              >
                <Popup>
                  <div className="popup-content">
                    <strong className={a.id === "agent-goldcoast" ? "text-gold" : "text-red"}>{a.code}</strong>
                    <span className="popup-status">{(a.drone ? "airborne" : a.status).toUpperCase()}</span>
                    <span className="popup-speed">{a.speed.toFixed(1)} km/h</span>
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {/* Live traces, for everyone. Hidden while the operator console is
              open so its own deletable markers aren't doubled up. Tapping one
              opens the tracker — the point is to lead people into the mechanic,
              not to hand them the answer, so no name and no distance here.

              `className` is a DIRECT prop, not part of pathOptions:
              react-leaflet feeds pathOptions through Leaflet's setStyle, which
              has no notion of className — only the constructor reads it, at
              _initPath time. Inside pathOptions it silently does nothing. */}
          {!adminOpen && traces.map((t) => (
            <Circle
              key={`trace-${t.id}`}
              center={traceRingCentre(t)}
              radius={TRACE_RING_RADIUS_M}
              className="trace-ring"
              pathOptions={{
                color: "#ef4444",
                weight: 1,
                opacity: 0.5,
                dashArray: "5 9",
                fillColor: "#ef4444",
                fillOpacity: 0.05,
              }}
              eventHandlers={{ click: () => setTraceScreenOpen(true) }}
            >
              <Popup>
                <div className="popup-content">
                  <strong className="text-red">TRACE SIGNAL</strong>
                  <span className="popup-sub">{t.region} · unscanned</span>
                  <span className="popup-sub">somewhere in this area</span>
                </div>
              </Popup>
            </Circle>
          ))}

          {/* Operator-only: every live trace as a deletable marker. */}
          {adminOpen && adminTraces.map((t) => (
            <Marker
              key={`op-trace-${t.id}`}
              position={[t.lat, t.lng]}
              icon={createAdminTraceIcon()}
              eventHandlers={{ click: () => deleteAdminTrace(t.id) }}
            >
              <Popup>
                <div className="popup-content">
                  <strong>{t.poi_name || "Trace"}</strong>
                  <span className="popup-sub">{t.region}</span>
                  <span className="popup-sub">tap marker to delete</span>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* POIs only for the selected, idle agent — keeps the map uncluttered. */}
          {selected && selected.status === "idle" && (selected.pois_near_current || []).map((p) => (
            <Marker
              key={`poi-${p.id}`}
              position={[p.location.lat, p.location.lng]}
              icon={L.divIcon({
                className: "poi-marker",
                html: `<div class="poi-dot ${p.type}"></div>`,
                iconSize: [8, 8],
                iconAnchor: [4, 4],
              })}
            >
              <Popup>
                <div className="popup-content">
                  <strong>{p.name}</strong>
                  <span className="popup-sub">{p.type.replace(/_/g, " ")}</span>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Active emotes from any watcher (fuzzed location, 5s lifetime) */}
          {emotes.map((e) => {
            const icon = createEmoteIcon(e.emoji_id);
            if (!icon) return null;
            const meta = getEmoteIcon(e.emoji_id);
            return (
              <Marker
                key={e.id}
                position={[e.lat, e.lng]}
                icon={icon}
              >
                <Popup>
                  <div className="popup-content">
                    <strong>Someone in {e.region}</strong>
                    <span className="popup-sub">sent a {meta ? meta.label : e.emoji_id}</span>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {/* Tracking banner — call-sign only. Assets never disclose a place
            name anywhere in the UI, so no origin/town is shown here. */}
        {selected && (
          <div className={`track-banner ${selected.id === "agent-goldcoast" ? "gold" : ""}`} data-testid="track-banner">
            <div className="track-banner-info">
              <span className="track-banner-code">TRACKING · {selected.code}</span>
            </div>
            <button
              className="track-banner-x"
              onClick={() => handleSelect(null)}
              data-testid="stop-tracking-x"
              title="Stop tracking"
              aria-label="Stop tracking"
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      <FleetPanel
        agents={agents}
        selectedId={selectedId}
        onSelect={handleSelect}
        follow={follow}
        setFollow={setFollow}
        open={trackedOpen}
        onToggle={toggleTracked}
      />

      <ActivityPanel
        activities={activities}
        now={now}
        open={activityOpen}
        onToggle={toggleActivity}
        onLocate={locateActivity}
      />

      {/* Floating TRACES tab on the right edge, below the ACTIVITY tab */}
      <button
        className="trace-tab"
        onClick={() => setTraceScreenOpen(true)}
        data-testid="trace-tab"
        title="Open Trace Tracker"
      >
        <Crosshair size={12} />
        <span className="trace-tab-label">TRACES</span>
        <span className="trace-tab-count" data-testid="trace-tab-count">{traces.length}</span>
      </button>

      <TraceTracker
        open={traceScreenOpen}
        onClose={() => setTraceScreenOpen(false)}
        userLocation={userLocation}
        watcherId={WATCHER_ID}
        soundEnabled={soundEnabled}
      />

      <EmoteBar
        watcherId={WATCHER_ID}
        cooldownSeconds={8}
        onSent={sendEmote}
        activeCount={emotes.length}
        onMark={handleMark}
        markState={markState}
      />

      {adminPrompt && (
        <div className="admin-pw-overlay" onClick={() => setAdminPrompt(false)}>
          <form className="admin-pw-box" onClick={(e) => e.stopPropagation()} onSubmit={submitAdminPw}>
            <div className="admin-pw-label">AUTHORIZATION REQUIRED</div>
            <input
              type="password"
              value={adminPw}
              onChange={(e) => setAdminPw(e.target.value)}
              autoFocus
              spellCheck={false}
              data-testid="admin-pw-input"
            />
          </form>
        </div>
      )}

      {adminOpen && (
        <AdminConsole
          agents={agents}
          armed={adminArmed}
          onArm={setAdminArmed}
          onHalt={handleAdminHalt}
          onBattery={handleAdminBattery}
          onState={handleAdminState}
          onInspect={handleSelect}
          traces={adminTraces}
          onDeleteTrace={deleteAdminTrace}
          onClose={() => { setAdminOpen(false); setAdminArmed(null); }}
        />
      )}

      {selfMarkWarn && (
        <div className="selfmark-overlay" onClick={() => setSelfMarkWarn(null)}>
          <div className="selfmark-box" onClick={(e) => e.stopPropagation()} data-testid="selfmark-warning">
            <div className="selfmark-title">⚠ THIS IS YOUR LOCATION</div>
            <p className="selfmark-body">
              You're about to drop a marker <strong>{selfMarkWarn.distance} m</strong> from
              where you are right now. Every viewer will see it, assets may come
              to investigate it, and it stays on the map for 5 minutes.
            </p>
            <p className="selfmark-body selfmark-em">
              Are you sure you want to show everyone where you are?
            </p>
            <div className="selfmark-actions">
              <button className="selfmark-cancel" onClick={() => setSelfMarkWarn(null)} data-testid="selfmark-cancel">
                CANCEL
              </button>
              <button
                className="selfmark-confirm"
                onClick={() => { setSelfMarkWarn(null); handleMark({ confirmedSelf: true }); }}
                data-testid="selfmark-confirm"
              >
                MARK IT ANYWAY
              </button>
            </div>
          </div>
        </div>
      )}

      {voteSpotlight && voteRound > 0 && (
        <div className="vote-spotlight" data-testid="vote-spotlight">
          <span className="vote-spotlight-dot" /> VIEWING <strong>{voteSpotlight}</strong>
        </div>
      )}

      {cullVote && (cullVote.finished || cullVote.resolved || (cullVote.deadline_ts * 1000) > Date.now()) && (
        <CullVote vote={cullVote} votedId={cullVotedId} onVote={handleCullVote} onLocate={handleCullLocate} />
      )}

      {proximityEvent && (
        <ProximityTakeover
          code={proximityEvent.code}
          signal={proximitySignal}
          onRequest={playChime}
          onDisable={handleProximityDisable}
        />
      )}

      <Briefing open={briefingOpen} onClose={() => setBriefingOpen(false)} />

      {showLoader && (
        <LoadingSequence
          onComplete={finishLoading}
          sessionId={sessionId}
          ready={tilesReady && agents.length > 0}
        />
      )}
    </div>
  );
}

export default App;
